import React, { useState, useEffect } from 'react';
import './App.css';
import ProductCatalog from './components/ProductCatalog';
import Cart from './components/Cart';
import UserProfile from './components/UserProfile';
import TopUpModal from './components/TopUpModal';

const API_BASE = 'https://high.magox.cfd/api';

function App() {
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopUp, setShowTopUp] = useState(false);
  const [tg, setTg] = useState(null);

  useEffect(() => {
    // Initialize Telegram Web App
    if (window.Telegram?.WebApp) {
      const telegram = window.Telegram.WebApp;
      setTg(telegram);
      
      // Configure the app
      telegram.ready();
      telegram.expand();
      
      // Set header color to match Telegram theme
      telegram.setHeaderColor(telegram.themeParams.bg_color || '#ffffff');
      
      // Get user info
      const telegramUser = telegram.initDataUnsafe?.user;
      if (telegramUser) {
        fetchUserData(telegramUser.id.toString());
      } else {
        // For development/testing, create a mock user
        const mockUserId = '123456789';
        fetchUserData(mockUserId);
      }
    } else {
      // For development outside Telegram
      const mockUserId = '123456789';
      fetchUserData(mockUserId);
    }

    fetchProducts();
  }, []);

  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/user/${userId}`);
      const userData = await response.json();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE}/products`);
      const productsData = await response.json();
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevCart, { ...product, quantity }];
      }
    });

    // Show haptic feedback if available
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred('light');
    }
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity === 0) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const handlePurchase = async () => {
    if (!user || cart.length === 0) return;

    const totalCost = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    if (user.balance < totalCost) {
      if (tg?.showAlert) {
        tg.showAlert('Insufficient balance! Please top up your account.');
      } else {
        alert('Insufficient balance! Please top up your account.');
      }
      setShowTopUp(true);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          items: cart.map(item => ({ productId: item.id, quantity: item.quantity })),
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Update user balance
        setUser(prevUser => ({ ...prevUser, balance: result.remainingBalance }));
        
        // Clear cart
        setCart([]);
        
        // Refresh products (update stock)
        fetchProducts();

        // Show success message
        if (tg?.showAlert) {
          tg.showAlert(`Purchase successful! Order #${result.orderId.slice(0, 8)}`);
        } else {
          alert(`Purchase successful! Order #${result.orderId.slice(0, 8)}`);
        }

        // Success haptic feedback
        if (tg?.HapticFeedback) {
          tg.HapticFeedback.notificationOccurred('success');
        }
      } else {
        throw new Error(result.error || 'Purchase failed');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      if (tg?.showAlert) {
        tg.showAlert(`Purchase failed: ${error.message}`);
      } else {
        alert(`Purchase failed: ${error.message}`);
      }

      // Error haptic feedback
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    }
  };

  const handleTopUp = async (amount, paymentMethod) => {
    try {
      const response = await fetch(`${API_BASE}/topup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          amount: parseFloat(amount),
          paymentMethod,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Update user balance
        setUser(prevUser => ({ ...prevUser, balance: result.newBalance }));
        setShowTopUp(false);

        // Success haptic feedback
        if (tg?.HapticFeedback) {
          tg.HapticFeedback.notificationOccurred('success');
        }

        if (tg?.showAlert) {
          tg.showAlert(result.message);
        } else {
          alert(result.message);
        }
      } else {
        throw new Error(result.error || 'Top-up failed');
      }
    } catch (error) {
      console.error('Top-up error:', error);
      if (tg?.showAlert) {
        tg.showAlert(`Top-up failed: ${error.message}`);
      } else {
        alert(`Top-up failed: ${error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading your shop...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="app-header">
        <h1>🛍️ Mini Shop</h1>
        {user && (
          <UserProfile 
            user={user} 
            onTopUp={() => setShowTopUp(true)} 
          />
        )}
      </header>

      <main className="app-main">
        <ProductCatalog 
          products={products} 
          onAddToCart={addToCart} 
        />
        
        {cart.length > 0 && (
          <Cart
            cart={cart}
            onUpdateQuantity={updateCartQuantity}
            onRemove={removeFromCart}
            onPurchase={handlePurchase}
            userBalance={user?.balance || 0}
          />
        )}
      </main>

      {showTopUp && (
        <TopUpModal
          onClose={() => setShowTopUp(false)}
          onTopUp={handleTopUp}
          currentBalance={user?.balance || 0}
        />
      )}
    </div>
  );
}

export default App;